import React from 'react';
import { View } from 'react-native';
import { Text } from '@/components/ui/text';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { WorkoutItem } from '~/workout/components/workout-item/WorkoutItem';
import { WorkoutUtils } from '@fytness/shared';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import type { WorkoutRecord } from '~/powersync/generated/schema';

interface WorkoutGroup {
  caption?: string | null;
  workoutIds: string[];
}

interface WorkoutListPayload {
  // Current shape: one or more captioned groups.
  groups?: WorkoutGroup[];
  // Back-compat: a single ungrouped list.
  workoutIds?: string[];
}

export interface WorkoutListCardProps {
  payload: Record<string, unknown>;
}

/** Normalise either payload shape into a list of groups. */
const toGroups = (payload: Record<string, unknown>): WorkoutGroup[] => {
  const p = payload as WorkoutListPayload;
  if (Array.isArray(p.groups)) return p.groups;
  if (Array.isArray(p.workoutIds)) return [{ workoutIds: p.workoutIds }];
  return [];
};

/** One captioned block: its own PowerSync query keeps the hook at top level. */
const WorkoutGroupBlock: React.FC<WorkoutGroup> = ({
  caption,
  workoutIds = [],
}) => {
  const { t } = useTranslation('workout');
  const router = useRouter();

  const { data: workouts, isLoading } = useLivePowerSyncQuery<WorkoutRecord>(
    workoutIds.length > 0 ? db.Workout.queries.findByIds(workoutIds) : null,
    [workoutIds],
  );

  if (isLoading) {
    return (
      <View className="w-full py-2 px-4 bg-muted rounded-lg">
        <Text className="text-muted-foreground text-sm">{t('loading')}</Text>
      </View>
    );
  }

  if (!workouts || workouts.length === 0) {
    return (
      <View className="w-full py-2 px-4 bg-muted rounded-lg">
        <Text className="text-muted-foreground text-sm">
          {caption || t('empty.title')}
        </Text>
      </View>
    );
  }

  const handleWorkoutPress = (workout: WorkoutRecord) => {
    const navigationPath = WorkoutUtils.getNavigationPath(workout);
    router.push({ pathname: navigationPath });
  };

  return (
    <View className="w-full gap-2">
      <Text className="text-sm text-muted-foreground px-1">
        {caption || t('workouts', { count: workouts.length })}
      </Text>
      {workouts.map((workout) => (
        <WorkoutItem
          key={workout.id}
          {...workout}
          exerciseCount={0}
          onPress={() => handleWorkoutPress(workout)}
        />
      ))}
    </View>
  );
};

export const WorkoutListCard: React.FC<WorkoutListCardProps> = ({
  payload,
}) => {
  const groups = toGroups(payload);

  if (groups.length === 0) {
    return null;
  }

  return (
    <View className="w-full py-2 gap-3">
      {groups.map((group, index) => (
        <WorkoutGroupBlock
          key={index}
          caption={group.caption}
          workoutIds={group.workoutIds ?? []}
        />
      ))}
    </View>
  );
};
